/**
 * Push-Benachrichtigungs-Einstellungen Komponente
 * Zeigt den Status und ermöglicht das Aktivieren/Deaktivieren von Push-Benachrichtigungen
 */
import React from 'react';
import { Bell, BellOff, Loader2, Clock } from 'lucide-react';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { usePushNotifications } from '../hooks/usePushNotifications';

const PushNotificationSettings = ({ compact = false }) => {
  const {
    isSupported,
    isEnabled,
    isLoading,
    permission,
    activeHours,
    currentlyActive,
    togglePushNotifications
  } = usePushNotifications();
  
  // Nicht unterstützt
  if (!isSupported) {
    if (compact) return null;
    
    return (
      <div className="text-sm text-slate-500 flex items-center gap-2">
        <BellOff className="w-4 h-4" />
        <span>Push-Benachrichtigungen werden nicht unterstützt</span>
      </div>
    );
  }
  
  // Berechtigung verweigert
  if (permission === 'denied') {
    if (compact) return null;
    
    return (
      <div className="text-sm text-amber-600 flex items-center gap-2">
        <BellOff className="w-4 h-4" />
        <span>Benachrichtigungen im Browser blockiert</span>
      </div>
    );
  }
  
  // Kompakte Ansicht (z.B. für Sidebar)
  if (compact) {
    return (
      <Button
        variant="ghost"
        size="sm"
        className="w-full justify-start text-blue-100 hover:text-white hover:bg-[#0A3A70]"
        onClick={togglePushNotifications}
        disabled={isLoading}
        data-testid="push-toggle-btn-compact"
      >
        {isLoading ? (
          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
        ) : isEnabled ? (
          <Bell className="w-4 h-4 mr-2" />
        ) : (
          <BellOff className="w-4 h-4 mr-2" />
        )}
        {isEnabled ? 'Push aktiv' : 'Push aktivieren'}
      </Button>
    );
  }
  
  // Vollständige Ansicht
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {isEnabled ? (
            <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
              <Bell className="w-5 h-5 text-emerald-600" />
            </div>
          ) : (
            <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
              <BellOff className="w-5 h-5 text-slate-400" />
            </div>
          )}
          <div>
            <Label htmlFor="push-toggle" className="text-base font-medium cursor-pointer">
              Push-Benachrichtigungen
            </Label>
            <p className="text-sm text-slate-500">
              {isEnabled 
                ? 'Benachrichtigungen sind aktiviert' 
                : 'Erhalten Sie Benachrichtigungen bei neuen Aufgaben'
              }
            </p>
          </div>
        </div>
        
        <Switch
          id="push-toggle"
          checked={isEnabled}
          onCheckedChange={togglePushNotifications}
          disabled={isLoading}
          data-testid="push-toggle-switch"
        />
      </div>
      
      {/* Aktive Zeiten Info */}
      {isEnabled && (
        <div className="bg-slate-50 rounded-lg p-3 flex items-start gap-3">
          <Clock className="w-5 h-5 text-slate-400 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-slate-700">
              Aktive Zeiten: {activeHours.start} - {activeHours.end} Uhr
            </p>
            <p className="text-xs text-slate-500 mt-1">
              {currentlyActive 
                ? '✓ Derzeit aktiv - Benachrichtigungen werden gesendet' 
                : '○ Derzeit Ruhezeit - keine Benachrichtigungen'
              }
            </p>
          </div>
        </div>
      )}
      
      {/* Hinweis bei deaktivierten Benachrichtigungen */}
      {!isEnabled && permission === 'default' && (
        <p className="text-xs text-slate-400">
          Ihr Browser wird Sie nach einer Berechtigung fragen.
        </p>
      )}
    </div>
  );
};

export default PushNotificationSettings;
